"use client";

import { useState, useCallback, useRef } from "react";
import {
  LayoutDashboard,
  Users,
  DollarSign,
  ClipboardCheck,
  Database,
  Shield,
  Settings,
  HelpCircle,
  HeartHandshake,
  LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import FaqAccordion from "./FaqAccordion";
import FaqSearchBar, { SearchResult } from "./FaqSearchBar";
import { faqCategories, FaqCategory } from "./faqData";

// ── Icon map ───────────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, LucideIcon> = {
  LayoutDashboard,
  Users,
  HeartHandshake,
  DollarSign,
  ClipboardCheck,
  Database,
  Shield,
  Settings,
  HelpCircle,
};

function CategoryIcon({ name, className }: { name: string; className?: string }) {
  const Icon = ICON_MAP[name] ?? HelpCircle;
  return <Icon className={cn("w-3.5 h-3.5 flex-shrink-0", className)} />;
}

// ── Category badge (used on accordion items) ──────────────────────────────────

function CategoryBadge({ category }: { category: FaqCategory }) {
  return (
    <span
      className={cn(
        "hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap",
        category.color
      )}
    >
      <CategoryIcon name={category.icon} />
      {category.label}
    </span>
  );
}

// ── Category nav pill ─────────────────────────────────────────────────────────

function CategoryPill({
  category,
  active,
  onClick,
}: {
  category: FaqCategory;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1",
        active
          ? "bg-blue-600 text-white border-blue-600 shadow-sm"
          : "hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200"
      )}
      style={
        active
          ? undefined
          : {
              borderColor: "var(--color-border)",
              color: "var(--color-text-secondary)",
              backgroundColor: "var(--color-bg-card)",
            }
      }
    >
      <CategoryIcon name={category.icon} />
      {category.label}
    </button>
  );
}

// ── Category section ──────────────────────────────────────────────────────────

function CategorySection({
  category,
  openId,
  onToggle,
}: {
  category: FaqCategory;
  openId: string | null;
  onToggle: (id: string) => void;
}) {
  return (
    <section id={`cat-${category.id}`} className="space-y-3 scroll-mt-6">
      {/* Section heading */}
      <div
        className={cn(
          "flex items-center gap-3 pb-3 border-b-2",
          // Use left border colour as bottom border
        )}
        style={{ borderColor: "var(--color-border)" }}
      >
        <div
          className={cn(
            "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border",
            category.color
          )}
        >
          <CategoryIcon name={category.icon} className="w-4 h-4" />
          {category.label}
        </div>
        <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>
          {category.items.length} {category.items.length === 1 ? "question" : "questions"}
        </span>
      </div>

      {/* Accordion items */}
      <div className="space-y-2">
        {category.items.map((item) => (
          <FaqAccordion
            key={item.id}
            id={item.id}
            question={item.question}
            answer={item.answer}
            isOpen={openId === item.id}
            onToggle={() => onToggle(item.id)}
          />
        ))}
      </div>
    </section>
  );
}

// ── Search results section ────────────────────────────────────────────────────

function SearchResults({
  results,
  openId,
  onToggle,
}: {
  results: SearchResult[];
  openId: string | null;
  onToggle: (id: string) => void;
}) {
  if (results.length === 0) {
    return (
      <div className="py-16 text-center space-y-3">
        <HelpCircle
          className="w-10 h-10 mx-auto opacity-30"
          style={{ color: "var(--color-text-muted)" }}
          aria-hidden="true"
        />
        <p className="text-sm font-medium" style={{ color: "var(--color-text-muted)" }}>
          No questions match your search.
        </p>
        <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
          Try different keywords or clear the search to browse all categories.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p
        className="text-sm font-medium"
        role="status"
        aria-live="polite"
        style={{ color: "var(--color-text-muted)" }}
      >
        {results.length} result{results.length !== 1 ? "s" : ""} found
      </p>
      <div className="space-y-2">
        {results.map(({ category, item }) => (
          <FaqAccordion
            key={item.id}
            id={`search-${item.id}`}
            question={item.question}
            answer={item.answer}
            isOpen={openId === `search-${item.id}`}
            onToggle={() => onToggle(`search-${item.id}`)}
            badge={<CategoryBadge category={category} />}
          />
        ))}
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function FaqPage() {
  const [activeCategory, setActiveCategory] = useState<string>(faqCategories[0].id);
  const [openId, setOpenId] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<SearchResult[] | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleToggle = useCallback((id: string) => {
    setOpenId((prev) => (prev === id ? null : id));
  }, []);

  const handleSearchResults = useCallback((results: SearchResult[] | null) => {
    setSearchResults(results);
    setOpenId(null);
  }, []);

  const handleCategoryChange = (id: string) => {
    setActiveCategory(id);
    setOpenId(null);
    // Scroll to section
    const el = document.getElementById(`cat-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const isSearching = searchResults !== null;
  const totalQuestions = faqCategories.reduce((sum, c) => sum + c.items.length, 0);

  return (
    <div className="min-h-full">

      {/* ── Hero / Header ─────────────────────────────────────────────────────── */}
      <div
        className="relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #dbeafe 0%, #eff6ff 40%, #e0f2fe 100%)",
          borderBottom: "1px solid var(--color-border)",
        }}
      >
        {/* Subtle decorative circles */}
        <div
          className="absolute -top-16 -right-16 w-64 h-64 rounded-full opacity-20 pointer-events-none"
          style={{ background: "radial-gradient(circle, #2563eb 0%, transparent 70%)" }}
          aria-hidden="true"
        />
        <div
          className="absolute -bottom-8 -left-8 w-40 h-40 rounded-full opacity-10 pointer-events-none"
          style={{ background: "radial-gradient(circle, #3b82f6 0%, transparent 70%)" }}
          aria-hidden="true"
        />

        <div className="relative max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-10 sm:pt-10 sm:pb-12">
          {/* Title + meta row */}
          <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
            <div>
              <h1
                className="text-2xl sm:text-3xl font-bold tracking-tight"
                style={{ color: "var(--color-text-primary)" }}
              >
                Frequently Asked Questions
              </h1>
              <p
                className="mt-2 text-sm sm:text-base max-w-xl leading-relaxed"
                style={{ color: "var(--color-text-secondary)" }}
              >
                Everything you need to know about the Meridian NDIS Analytics Platform
              </p>
            </div>
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium shrink-0"
              style={{
                backgroundColor: "rgba(255,255,255,0.7)",
                color: "var(--color-text-muted)",
                border: "1px solid var(--color-border)",
                backdropFilter: "blur(4px)",
              }}
            >
              <HelpCircle className="w-3.5 h-3.5" aria-hidden="true" />
              {totalQuestions} questions &middot; {faqCategories.length} categories
            </div>
          </div>

          {/* Search bar */}
          <FaqSearchBar onResults={handleSearchResults} />
        </div>
      </div>

      {/* ── Main content ─────────────────────────────────────────────────────── */}
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6">

        {/* Category navigation (hidden during search) */}
        {!isSearching && (
          <>
            {/* Desktop: horizontal pill row */}
            <div className="hidden sm:flex flex-wrap gap-2">
              {faqCategories.map((cat) => (
                <CategoryPill
                  key={cat.id}
                  category={cat}
                  active={activeCategory === cat.id}
                  onClick={() => handleCategoryChange(cat.id)}
                />
              ))}
            </div>

            {/* Mobile: select dropdown */}
            <div className="sm:hidden">
              <select
                value={activeCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                aria-label="Jump to FAQ category"
                className="w-full px-3 py-2.5 text-sm rounded-xl border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{
                  backgroundColor: "var(--color-bg-card)",
                  borderColor: "var(--color-border)",
                  color: "var(--color-text-primary)",
                }}
              >
                {faqCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {/* Content: search results OR all category sections */}
        {isSearching ? (
          <SearchResults
            results={searchResults!}
            openId={openId}
            onToggle={handleToggle}
          />
        ) : (
          <div ref={contentRef} className="space-y-10 sm:space-y-12">
            {faqCategories.map((cat) => (
              <CategorySection
                key={cat.id}
                category={cat}
                openId={openId}
                onToggle={handleToggle}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
